import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button, Grid, Paper } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

const API_URL = process.env.REACT_APP_GOLANGAPI || 'http://localhost:8000';

const plans = [
  {
    name: 'Monthly',
    price: '10',
    period: 'month',
    features: [
      'Unlimited PCR reports',
      'Use of templates included',
      'Customize your feedback',
      'Upload your own protocol document'
    ],
    lookup_key: 'monthly_subscription',
    popular: false
  },
  {
    name: 'Yearly',
    price: '100',
    period: 'year',
    features: [
      'Everything in Monthly plan',
      'Save $20 annually',
    ],
    lookup_key: 'yearly_subscription',
    popular: true
  }
];

const PlanCard = ({ plan }) => (
  <Paper
    elevation={plan.popular ? 8 : 3}
    sx={{
      p: 3,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      transition: 'transform 0.2s ease-in-out',
      border: plan.popular ? '2px solid #2563eb' : '1px solid #e2e8f0',
      '&:hover': {
        transform: 'translateY(-4px)'
      }
    }}
  >
    {plan.popular && (
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: '#2563eb',
          color: 'white',
          px: 1.5,
          py: 0.5,
          borderRadius: '8px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}
      >
        Best Value
      </Box>
    )}
    
    <Typography variant="h6" component="h2" fontWeight={700} gutterBottom>
      {plan.name}
    </Typography>
    
    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
      <Typography variant="h4" component="span" fontWeight={800}>
        ${plan.price}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
        / {plan.period}
      </Typography>
    </Box>

    <Box sx={{ flexGrow: 1 }}>
      {plan.features.map((feature, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <CheckCircleIcon sx={{ color: '#2563eb', mr: 1, fontSize: '1rem' }} />
          <Typography variant="body2">{feature}</Typography>
        </Box>
      ))}
    </Box>

    <Box component="form" action={`${API_URL}/api/stripe/create-checkout-session`} method="POST" sx={{ mt: 2 }}>
      <input type="hidden" name="lookup_key" value={plan.lookup_key} />
      <Button
        type="submit"
        variant={plan.popular ? "contained" : "outlined"}
        fullWidth
        size="medium"
        sx={{
          py: 1,
          borderRadius: '8px',
          fontWeight: 600,
          backgroundColor: plan.popular ? '#2563eb' : 'transparent',
          '&:hover': {
            backgroundColor: plan.popular ? '#1d4ed8' : 'rgba(37, 99, 235, 0.04)'
          }
        }}
      >
        Get Started
      </Button>
    </Box>
  </Paper>
);

const SuccessDisplay = ({ sessionId }) => (
  <Container maxWidth="lg" sx={{ pt: 1, pb: 4 }}>
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Paper style={{ padding: '20px', marginTop: '20px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <StarIcon sx={{ fontSize: 48, color: '#2563eb', mb: 2 }} />
          <Typography variant="h5" component="h1" gutterBottom>
            Thank you!
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Your subscription has been activated successfully.
          </Typography>
          <Box component="form" action={`${API_URL}/api/stripe/create-portal-session`} method="POST" sx={{ mt: 3 }}>
            <input type="hidden" id="session-id" name="session_id" value={sessionId} />
            <Button
              type="submit"
              variant="outlined"
              size="medium"
              sx={{
                py: 1,
                borderRadius: '8px',
                fontWeight: 600
              }}
            >
              Manage Subscription
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  </Container>
);

const Message = ({ message }) => (
  <Container maxWidth="lg" sx={{ pt: 1, pb: 4 }}>
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Paper style={{ padding: '20px', marginTop: '20px' }}>
        <Typography variant="body2" align="center" color="text.secondary">
          {message}
        </Typography>
      </Paper>
    </Box>
  </Container>
);

export default function Subscription() {
  let [message, setMessage] = useState('');
  let [success, setSuccess] = useState(false);
  let [sessionId, setSessionId] = useState('');

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    if (query.get('success')) {
      setSuccess(true);
      setSessionId(query.get('session_id'));
    }

    if (query.get('canceled')) {
      setSuccess(false);
      setMessage(
        "Subscription canceled. Feel free to explore our plans and subscribe when you're ready."
      );
    }
  }, [sessionId]);

  if (!success && message === '') {
    return (
      <Container maxWidth="lg" sx={{ pt: 1, pb: 4 }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Paper style={{ padding: '20px', marginTop: '20px' }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1" fontWeight={800} gutterBottom>
                Choose Your Plan
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                Get unlimited access to all features. Choose between monthly or yearly billing.
              </Typography>
            </Box>
            <Grid container spacing={3} justifyContent="center">
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} key={plan.name}>
                  <PlanCard plan={plan} />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      </Container>
    );
  } else if (success && sessionId !== '') {
    return <SuccessDisplay sessionId={sessionId} />;
  } else {
    return <Message message={message} />;
  }
}